// import React from 'react';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { AffinityDto } from 'models/dtoTypes';
import { smartGovernancePrimaryColor } from 'kosmosConfig';

const PREFIX = '_euaffinitySlider';

const classes = {
  dot: `${PREFIX}-dot`,
  dots: `${PREFIX}-dots`,
  secondaryAffinity: `${PREFIX}-secondaryAffinity`,
};

const Labels = styled('div')(() => ({
  width: '100%',
  display: 'flex',
  '& > *': {
    flex: 1,
  }
}));

const Chart = styled('div')(() => ({
  position: 'relative',
  width: '100%',
  height: 50,
  [`& .${classes.dots}`]: {
    position: 'relative',
    display: 'flex',
    height: '100%',
    marginRight: 50,
  },
  [`& .${classes.dot}`]: {
    flex: 1,
  },

  [`& .${classes.secondaryAffinity}`]: {
    width: '100%',
    height: '100%',
    position: 'relative',
    '& > p': {
      position: 'absolute',
      top: -25,
      margin: 0,
      width: '100%',
      textAlign: 'center',
      color: smartGovernancePrimaryColor,
    },
    '& > div': {
      position: 'absolute',
      top: '50%',
      left: '50%',
      marginTop: -10,
      marginLeft: -10,
      borderRadius: 10,
      backgroundColor: smartGovernancePrimaryColor,
      width: 20,
      height: 20,
    }
  },
}));

interface IMark {
  label: string;
  left: string;
  color: string;
  border?: boolean;
  hasSecondary: boolean;
}

const Rails = styled('div')(() => ({
  position: 'absolute',
  width: '100%',
  height: 6,
  left: 0,
  top: '50%',
  marginTop: -3,
  backgroundColor: '#d6d6d6',
}));

const Dot = styled('div', {
  shouldForwardProp: (prop) => prop !== 'left' && prop !== 'color',
}) <{ left: string; color: string }>
(({ color, left }) => ({
  position: 'absolute',
  top: '50%',
  marginTop: -25,
  left: left,
  height: 50,
  width: 50,
  borderRadius: 25,
  backgroundColor: color,
  '&.border': {
    border: 'solid 1px black',
  }
}));

interface IProps {
  title: string;
  affinity?: AffinityDto;
  secondaryAffinity?: AffinityDto;
}

export default function EuOptionSlider(props: IProps): JSX.Element {
  const { affinity, secondaryAffinity, title } = props;

  const generateMarksSchema = () => {
    const gray = '#d6d6d6';
    return [
      {
        value: 0,
        label: 'one',
        left: '0%',
        color: affinity === 'negative' ? '#fd5657' : gray, // red
        hasSecondary: secondaryAffinity === 'negative',
      },
      {
        value: 1,
        label: 'one',
        left: '33.33%',
        color: affinity === 'warning' ? '#e6b130' : gray, // amber
        hasSecondary: secondaryAffinity === 'warning',
      },
      {
        value: 2,
        label: 'two',
        left: '66.66%',
        color: affinity === 'neutral' ? '#ffffff' : gray, // white
        border: affinity === 'neutral',
        hasSecondary: secondaryAffinity === 'neutral',
      },
      {
        value: 3,
        label: 'three',
        left: '100%',
        color: affinity === 'positive' ? '#24c8a6' : gray, // blue
        hasSecondary: secondaryAffinity === 'positive',
      },
    ];
  };

  const renderSecondaryAffinity = () => {
    return (
      <div className={classes.secondaryAffinity}>
        <p>SG</p>
        <div className="blue-dot"></div>
      </div>
    );
  };

  const renderDot = (mark: IMark, index: number) => {
    const { color, left, border, hasSecondary } = mark;
    return (
      <Dot
        key={index}
        color={color}
        left={left}
        className={border ? 'border' : ''}
      >
        {hasSecondary && renderSecondaryAffinity()}
      </Dot>
    );
  };

  return (
    <div>
      <Typography
        variant="h5"
        gutterBottom
      >
        {title}
      </Typography>
      <Chart>
        <Rails />
        <div className={classes.dots}>
          {generateMarksSchema().map(renderDot)}
        </div>
      </Chart>
      <Labels>
        <Typography variant="body1" sx={{ textAlign: 'left' }}>Significant Harm Found</Typography>
        <Typography variant="body1" sx={{ textAlign: 'left' }}>Opportunity For Improvement</Typography>
        <Typography variant="body1" sx={{ textAlign: 'right' }}>No Significant Harm</Typography>
        <Typography variant="body1" sx={{ textAlign: 'right' }}>Positively Contributing</Typography>
      </Labels>
    </div>
  );
}

